import React, { useState } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { CERULEAN } from '../@style/colors';
import { DownloadFormSchema } from '../form/validators';
import SubmitButton from './submitButton';
import CheckBox from './ui/checkbox';
import { Formik, Field, Form } from 'formik';
import { RequestBody, sendUserInfo } from '../api/downloadForm.service';
import Logo from '../images/logo.svg';
import screens from '../@style/screens';
import ReCAPTCHA from 'react-google-recaptcha';

interface DownloadFormProps {
  onDone: () => void;
}

interface FieldWrapperProps {
  width?: string;
  marginRight?: string;
  marginLeft?: string;
}

const DownloadFormWrapper = styled.div`
  height: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media ${screens.laptop} {
    padding: 0 1rem;
  }
`;

const FieldWrapper = styled.div<FieldWrapperProps>`
  width: 100%;
  input {
    width: 100%;
    padding-right: ${(props) => (props.marginRight ? props.marginRight : '0')};
    padding-left: ${(props) => (props.marginLeft ? props.marginLeft : '0')};
    height: 45px;
    border: none;
    color: white;
    border-bottom: 2px solid ${CERULEAN};
    font-size: 20px;
    font-weight: 400;
    background: none;
    margin-bottom: 5px;
    &::placeholder {
      color: white;
      font-size: 18px;
    }
    &:focus {
      outline: none;
      transition: 0.8s;
      border-bottom: 2px solid white;
    }
    @media ${screens.laptop} {
      font-size: 24px;
      height: 64px;
    }
  }
  div {
    font-size: 14px;
  }
`;

const CheckboxCopy = styled.div`
  width: 100%;
`;

const NamesWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
  width: 100%;
  @media ${screens.laptop} {
    grid-gap: 2rem;
  }
`;

const EmailRow = styled.div`
  display: flex;
  width: 100%;
  margin-top: 1rem;
  @media ${screens.laptop} {
    margin-top: 0;
  }
`;

const TermsConditionsWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const Title = styled.h2`
  padding: 2rem 0;
  font-size: 24px;
  font-weight: 400;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Label = styled.h4`
  font-size: 32px;
  font-weight: 700;
  @media ${screens.laptop} {
    margin-left: 20px;
  }
`;

const LogoSmaller = styled(Logo)`
  width: 77px;
  height: 35px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
`;

const RecaptchaRow = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 10px 0;
`;

const DownloadForm: React.FC<DownloadFormProps> = ({ onDone }) => {
  const [token, setToken] = useState('');
  const recaptchaRef = React.createRef();

  const body: RequestBody = {
    firstName: '',
    lastName: '',
    email: '',
    agreeTerms: false,
    agreeSuscription: false,
    token: '',
  };

  const refreshToken = () => {
    const newToken = recaptchaRef?.current?.getValue?.();
    setToken(newToken);
  };

  return (
    <DownloadFormWrapper>
      <HeaderWrapper>
        <Link to="/">
          <LogoSmaller />
        </Link>

        <Link to="/">
          <Label>OPEN BUILDER</Label>
        </Link>
      </HeaderWrapper>
      <Title>Enter your information to begin download</Title>
      <Formik<RequestBody>
        initialValues={body}
        validationSchema={DownloadFormSchema}
        onSubmit={(data) => {
          sendUserInfo({ ...data, token }).then((resp) => {
            onDone();
          });
        }}
      >
        {({ errors, touched, setFieldValue, setFieldTouched, dirty, isValid, isSubmitting }) => (
          <StyledForm name="download-form">
            <NamesWrapper>
              <FieldWrapper>
                <Field name="firstName" placeholder="First name" />
                {errors.firstName && touched.firstName ? <div>{errors.firstName}</div> : null}
              </FieldWrapper>
              <FieldWrapper>
                <Field name="lastName" placeholder="Last name" />
                {errors.lastName && touched.lastName ? <div>{errors.lastName}</div> : null}
              </FieldWrapper>
            </NamesWrapper>
            <EmailRow>
              <FieldWrapper>
                <Field name="email" type="email" placeholder="Email Address" />
                {errors.email && touched.email ? <div>{errors.email}</div> : null}
              </FieldWrapper>
            </EmailRow>
            <TermsConditionsWrapper>
              <CheckBox
                checked={body.agreeTerms}
                onClick={() => {
                  setFieldValue('agreeTerms', !body.agreeTerms, true);
                  body.agreeTerms = !body.agreeTerms;
                  setFieldTouched('agreeTerms', true, false);
                }}
              >
                <CheckboxCopy>
                  I agree to abide by the terms of the license and &nbsp;
                  <a href="/terms" style={{ textDecoration: 'underline' }} target="_blank">
                    Terms of Use &nbsp;
                  </a>
                </CheckboxCopy>
              </CheckBox>
              {errors.agreeTerms && touched.agreeTerms ? <div>{errors.agreeTerms}</div> : null}

              <CheckBox
                checked={body.agreeSuscription}
                onClick={() => {
                  setFieldValue('agreeSuscription', !body.agreeSuscription, true);
                  body.agreeSuscription = !body.agreeSuscription;
                  setFieldTouched('agreeSuscription', true, false);
                }}
              >
                <CheckboxCopy>
                  Sign up for Lowe's email offers, special promotions, project inspiration, savings,
                  and more. &nbsp;
                  <a
                    href="https://www.lowes.com/l/about/privacy-and-security-statement"
                    style={{ textDecoration: 'underline' }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Statement.
                  </a>
                </CheckboxCopy>
              </CheckBox>
            </TermsConditionsWrapper>
            <RecaptchaRow>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.RECAPTCHA_PUBLIC_TOKEN}
                onChange={() => refreshToken()}
              />
            </RecaptchaRow>
            <SubmitButton
              label="Start Download"
              disabled={!isValid || !dirty || isSubmitting || !token}
            />
          </StyledForm>
        )}
      </Formik>
    </DownloadFormWrapper>
  );
};

export default DownloadForm;
